import { useState } from "react";

import { useIntl } from "react-intl";

import { Button } from "@ps-design-system/components/Button";
import { LocaleSelectorModal } from "@ps-design-system/components/LocaleSelectorModal";
import GlobeIcon20 from "@ps-design-system/icons/Globe/Globe20";

import { LocaleData } from "@/i18n/i18n.types";
import { useIntlContext } from "@/i18n/IntlProvider";
import { updateProfile } from "@/services/profile.service";
import { useAuthStore } from "@/store/auth.store";
import { useProfileStore } from "@/store/profile.store";
import { trackEvent } from "@/utils/analytics.utils";

import { savePreferredLocaleCookie } from "./LocaleSelector.utils";

export const LocaleSelector = () => {
  const intl = useIntl();
  const { locale, localeDataMap } = useIntlContext();

  const { accessToken } = useAuthStore();
  const { profile } = useProfileStore();

  const [profileUpdateLoading, setProfileUpdateLoading] = useState(false);

  /**
   * Handles analytics, saving the preferred locale cookie, updating the profile,
   * and reloading the page to apply the new locale.
   * @param selectedLocaleData - The selected locale data.
   */
  const handleSelectLocale = async (selectedLocaleData: LocaleData) => {
    if (
      !selectedLocaleData?.translations ||
      selectedLocaleData.translations === locale
    ) {
      return;
    }

    trackEvent("locale updated", {
      changed_from: locale,
      changed_to: selectedLocaleData.translations,
      ui_element: "navbar",
      ssoid: profile?.SSOid,
    });
    savePreferredLocaleCookie(selectedLocaleData.translations);

    if (accessToken) {
      // If user is logged in, update the profile first
      try {
        setProfileUpdateLoading(true);
        await updateProfile({
          accessToken,
          payload: {
            preferred_locale: selectedLocaleData.translations,
          },
        });
      } catch {
        // gracefully ignore error, continue to take the user to the new path
      }
    }

    // Reload the page to apply the new locale
    window.location.reload();
  };

  return (
    <LocaleSelectorModal
      intl={intl}
      trigger={
        <Button
          variant="text-only"
          appearance="white"
          iconOnly
          ariaLabel="Change language"
        >
          <GlobeIcon20 aria-hidden="true" />
        </Button>
      }
      profileUpdateLoading={profileUpdateLoading}
      profileUpdateHasError={false}
      activeLocales={localeDataMap}
      onSelectLocale={(localeData) =>
        handleSelectLocale(localeData as LocaleData)
      }
      currentLocale={locale}
    />
  );
};
